/**
 * AL × RL pattern and suggested talking point for the Pre-call Brief.
 * Ported from the v4 mock's PATTERNS / alBand() / rlBand() / getPattern()
 * (my-accounts-portfolio-v4.html lines 8190-8205): a fixed lookup on the
 * account's AL band and RL band — no AI, no other account data.
 */

export type Band = "low" | "mid" | "high";

export interface BriefPattern {
  name: string;
  talking: string;
}

const PATTERNS: Record<`${Band}-${Band}`, BriefPattern> = {
  "high-high": {
    name: "Strategic Alignment",
    talking:
      "Leverage the strong strategic alignment and relationship depth to expand scope — this account is ready for a growth conversation.",
  },
  "high-mid": {
    name: "Dependent Alignment",
    talking:
      "Strategic alignment is solid but rests on a narrow relationship base. Use this call to identify and add at least one additional executive contact.",
  },
  "high-low": {
    name: "Fragile Alignment",
    talking:
      "Alignment is strong but personal executive relationships are thin — a single leadership change could collapse the program. Broaden the relationship base now.",
  },
  "mid-high": {
    name: "Ready to Advance",
    talking:
      "Strong executive relationships are in place but strategic alignment is mid-level. Use the existing relationship warmth as the entry point for the alignment conversation.",
  },
  "mid-mid": {
    name: "Building Momentum",
    talking:
      "Both alignment and relationships are developing positively. Identify the next milestone that will move AL forward and make it the focus of this call.",
  },
  "mid-low": {
    name: "Hollow Alignment",
    talking:
      "Alignment metrics look decent but relationship depth is thin — progress is fragile. Focus this call on identifying a personal executive champion.",
  },
  "low-high": {
    name: "Warm but Uncommitted",
    talking:
      "Executive relationships are strong but strategic alignment is low — the relationship capital is in place. Use this call to surface an alignment gap and get commitment to address it.",
  },
  "low-mid": {
    name: "Emerging Relationship",
    talking:
      "Relationship development is underway. Use this call to deepen one key executive relationship and ask what business outcome matters most to them right now.",
  },
  "low-low": {
    name: "Stalled Relationship",
    talking:
      "Both alignment and relationships are weak. Use this call to establish a personal connection with at least one executive stakeholder — start small, focus on listening.",
  },
};

/** AL0–1 low, AL2–3 mid, AL4–5 high (mock alBand). */
export function alBand(al: number): Band {
  if (al <= 1) return "low";
  return al <= 3 ? "mid" : "high";
}

/** RL0–1 low, RL2–3 mid, RL4–5 high (mock rlBand). */
export function rlBand(rl: number): Band {
  if (rl <= 1) return "low";
  return rl <= 3 ? "mid" : "high";
}

/**
 * The account's pattern, or null when AL or RL hasn't been assessed. The
 * mock treated a missing level as "low"; a real unassessed account would
 * then be told it has a "Stalled Relationship", which we don't know.
 */
export function briefPattern(
  al: number | null,
  rl: number | null
): BriefPattern | null {
  if (al == null || rl == null) return null;
  return PATTERNS[`${alBand(al)}-${rlBand(rl)}`];
}
